import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ShipStatusTracker from './ship-status-tracker';

interface SectionHP {
  current: number;
  max: number;
  hasDealtHoleDamage?: boolean;
}

interface InteractiveShipHullProps {
  upperDeckImage: string;
  bottomDeckImage: string;
  upperDeckSections: SectionHP[];
  bottomDeckSections: SectionHP[];
  riggingSections: SectionHP[];
  onUpperDeckUpdate: (sections: SectionHP[]) => void;
  onBottomDeckUpdate: (sections: SectionHP[]) => void;
  onRiggingUpdate: (sections: SectionHP[]) => void;
}

const InteractiveShipHull: React.FC<InteractiveShipHullProps> = ({ 
  upperDeckImage, 
  bottomDeckImage,
  upperDeckSections,
  bottomDeckSections,
  riggingSections,
  onUpperDeckUpdate,
  onBottomDeckUpdate,
  onRiggingUpdate
}) => {
  const [isUpperDeck, setIsUpperDeck] = useState(true);
  const [selectedSection, setSelectedSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);
  const [hpAdjustment, setHpAdjustment] = useState<number>(0);
  const [flashingSection, setFlashingSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);

  const riggingLabels = ["Foremast", "Main Mast", "Aft Mast"];

  const handleSectionClick = (index: number, type: 'hull' | 'rigging') => {
    setSelectedSection({ type, index });
  };

  const handleHPAdjustment = (value: number) => {
    if (!selectedSection) return;

    if (selectedSection.type === 'rigging') {
      const newSections = [...riggingSections];
      const currentHP = newSections[selectedSection.index];
      newSections[selectedSection.index] = {
        ...currentHP,
        current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
      };
      onRiggingUpdate(newSections);
    } else {
      // Hull section
      if (isUpperDeck) {
        const newSections = [...upperDeckSections];
        const currentHP = newSections[selectedSection.index];
        newSections[selectedSection.index] = {
          ...currentHP,
          current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
        };
        onUpperDeckUpdate(newSections);
      } else {
        const newSections = [...bottomDeckSections];
        const currentHP = newSections[selectedSection.index];
        newSections[selectedSection.index] = {
          ...currentHP,
          current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
        };
        onBottomDeckUpdate(newSections);
      }
    }
  };

  const handleRandomDamage = () => {
    const allSections = [...upperDeckSections, ...bottomDeckSections, ...riggingSections];
    const randomIndex = Math.floor(Math.random() * allSections.length);

    if (randomIndex < upperDeckSections.length) {
      // Upper deck section
      const newSections = [...upperDeckSections];
      newSections[randomIndex] = {
        ...newSections[randomIndex],
        current: Math.max(0, newSections[randomIndex].current - hpAdjustment)
      };
      onUpperDeckUpdate(newSections);
      setIsUpperDeck(true);
      setFlashingSection({ type: 'hull', index: randomIndex });
      setSelectedSection({ type: 'hull', index: randomIndex });
    } else if (randomIndex < upperDeckSections.length + bottomDeckSections.length) {
      // Bottom deck section
      const bottomIndex = randomIndex - upperDeckSections.length;
      const newSections = [...bottomDeckSections];
      newSections[bottomIndex] = {
        ...newSections[bottomIndex],
        current: Math.max(0, newSections[bottomIndex].current - hpAdjustment)
      };
      onBottomDeckUpdate(newSections);
      setIsUpperDeck(false);
      setFlashingSection({ type: 'hull', index: bottomIndex });
      setSelectedSection({ type: 'hull', index: bottomIndex });
    } else {
      // Rigging section
      const riggingIndex = randomIndex - (upperDeckSections.length + bottomDeckSections.length);
      const newSections = [...riggingSections];
      newSections[riggingIndex] = {
        ...newSections[riggingIndex],
        current: Math.max(0, newSections[riggingIndex].current - hpAdjustment)
      };
      onRiggingUpdate(newSections);
      setFlashingSection({ type: 'rigging', index: riggingIndex });
      setSelectedSection({ type: 'rigging', index: riggingIndex });
    }

    // Clear flashing after a delay
    setTimeout(() => setFlashingSection(null), 1000);
  };

  const getColor = (hp: SectionHP, isFlashing: boolean): string => {
    if (isFlashing) return 'rgba(255, 0, 0, 0.5)'; // Flashing red for damage
    if (hp.current <= 0) return 'rgba(128, 0, 0, 0.5)'; // Destroyed
    if (hp.current <= hp.max / 2) return 'rgba(255, 255, 0, 0.5)'; // Damaged
    return 'rgba(0, 255, 0, 0)'; // Normal
  };

  const toggleDeck = () => {
    setIsUpperDeck(!isUpperDeck);
    setSelectedSection(null);
  };

  const handleDealHoledDamage = () => {
    const dealDamageToAdjacentSections = (sections: SectionHP[], index: number) => {
      const isLeftEdge = index % 2 === 0;
      const isRightEdge = index % 2 === 1;
      const isTopEdge = index < 2;
      const isBottomEdge = index >= sections.length - 2;

      const adjacentIndices: number[] = [];

      if (!isLeftEdge) adjacentIndices.push(index - 1); // Left
      if (!isRightEdge) adjacentIndices.push(index + 1); // Right
      if (!isTopEdge) adjacentIndices.push(index - 2); // Above
      if (!isBottomEdge) adjacentIndices.push(index + 2); // Below

      return sections.map((section, i) => {
        if (adjacentIndices.includes(i)) {
          return {
            ...section,
            current: Math.max(0, section.current - 40)
          };
        }
        return section;
      });
    };

    // Update upper deck sections
    let newUpperDeckSections = [...upperDeckSections];
    let didUpdateUpper = false;
    
    upperDeckSections.forEach((section, index) => {
      if (section.current <= 0 && !section.hasDealtHoleDamage) {
        newUpperDeckSections = dealDamageToAdjacentSections(newUpperDeckSections, index);
        newUpperDeckSections[index] = { ...newUpperDeckSections[index], hasDealtHoleDamage: true };
        didUpdateUpper = true;
      }
    });
    
    if (didUpdateUpper) {
      onUpperDeckUpdate(newUpperDeckSections);
    }

    // Update bottom deck sections
    let newBottomDeckSections = [...bottomDeckSections];
    let didUpdateBottom = false;
    
    bottomDeckSections.forEach((section, index) => {
      if (section.current <= 0 && !section.hasDealtHoleDamage) {
        newBottomDeckSections = dealDamageToAdjacentSections(newBottomDeckSections, index);
        newBottomDeckSections[index] = { ...newBottomDeckSections[index], hasDealtHoleDamage: true };
        didUpdateBottom = true;
      }
    });
    
    if (didUpdateBottom) {
      onBottomDeckUpdate(newBottomDeckSections);
    }
  };

  const handleRepairAll = () => {
    const repairedUpperDeck = upperDeckSections.map(section => 
      ({ ...section, current: section.max, hasDealtHoleDamage: false })
    );
    onUpperDeckUpdate(repairedUpperDeck);
    
    const repairedBottomDeck = bottomDeckSections.map(section => 
      ({ ...section, current: section.max, hasDealtHoleDamage: false })
    );
    onBottomDeckUpdate(repairedBottomDeck);
    
    const repairedRigging = riggingSections.map(section => 
      ({ ...section, current: section.max })
    );
    onRiggingUpdate(repairedRigging);
  };

  const currentDeckSections = isUpperDeck ? upperDeckSections : bottomDeckSections;
  const currentDeckImage = isUpperDeck ? upperDeckImage : bottomDeckImage;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-bold">{isUpperDeck ? 'Upper Deck' : 'Lower Deck'}</h2>
        <Button onClick={toggleDeck}>
          Switch to {isUpperDeck ? 'Lower' : 'Upper'} Deck
        </Button>
      </div>
      <div className="flex">
        {/* Rigging sections */}
        <div className="w-1/5 mr-2">
          <h3 className="text-sm font-semibold mb-1">Rigging</h3>
          <div className="flex flex-col space-y-2">
            {riggingSections.map((hp, index) => (
              <div 
                key={index} 
                className={`flex flex-col border border-gray-300 rounded p-1 cursor-pointer ${
                  selectedSection?.type === 'rigging' && selectedSection.index === index ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => handleSectionClick(index, 'rigging')}
              >
                <label className="text-xs mb-1 dark:text-white">{riggingLabels[index]}</label>
                <div
                  className="h-8 flex items-center justify-center"
                  style={{ 
                    backgroundColor: getColor(hp, flashingSection?.type === 'rigging' && flashingSection.index === index)
                  }}
                >
                  <div className="text-xs text-center text-black dark:text-white font-bold">{hp.current}/{hp.max}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Add ShipStatusTracker here */}
          <ShipStatusTracker
            upperDeckSections={upperDeckSections}
            bottomDeckSections={bottomDeckSections}
            riggingSections={riggingSections}
          />
        </div>
        {/* Ship hull */}
        <div className="relative w-3/5">
          <img 
            src={currentDeckImage}
            alt={`${isUpperDeck ? 'Upper' : 'Lower'} Deck Hull`} 
            className="w-full"
          />
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-6">
            {currentDeckSections.map((hp, index) => (
              <div
                key={index}
                className={`border border-white transition-colors flex items-center justify-center cursor-pointer ${
                  selectedSection?.type === 'hull' && selectedSection.index === index ? 'ring-2 ring-blue-500' : ''
                }`}
                style={{ 
                  backgroundColor: getColor(hp, flashingSection?.type === 'hull' && flashingSection.index === index),
                  position: 'absolute',
                  width: '50%',
                  height: '16.67%',
                  left: index % 2 === 0 ? '0' : '50%',
                  top: `${Math.floor(index / 2) * 16.67}%`
                }}
                onClick={() => handleSectionClick(index, 'hull')}
              >
                <div className="text-xs text-center text-black dark:text-white font-bold">{hp.current}/{hp.max}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Control Panel */}
        <div className="w-1/5 ml-2">
          <h3 className="text-sm font-semibold mb-1">HP Adjustment</h3>
          <div className="flex flex-col space-y-2">
            <Input 
              type="number" 
              value={hpAdjustment}
              onChange={(e) => setHpAdjustment(parseInt(e.target.value) || 0)}
              placeholder="Enter HP change"
            />
            <Button onClick={() => handleHPAdjustment(hpAdjustment)} disabled={!selectedSection}>
              Add HP
            </Button>
            <Button onClick={() => handleHPAdjustment(-hpAdjustment)} disabled={!selectedSection}>
              Subtract HP
            </Button>
            <Button onClick={handleRandomDamage} disabled={hpAdjustment <= 0}>
              Deal Random Damage
            </Button>
            <Button onClick={handleDealHoledDamage}>
              Deal Holed Damage
            </Button>
            <Button onClick={handleRepairAll}>
              Repair All
            </Button>
          </div>
          {selectedSection && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold">Selected Section:</h4>
              <p className="text-xs">
                {selectedSection.type === 'rigging' 
                  ? `${riggingLabels[selectedSection.index]} Rigging` 
                  : `Hull Section ${selectedSection.index + 1}`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveShipHull;